import React, { FunctionComponent } from 'react';
import { EditOutlined } from '@mui/icons-material';
import { Typography } from '@mui/material';
import i18next from 'i18next';

import { history } from 'helpers/history';
import { formatHebrewFromISO } from 'components/inputs/HebrewDateInput';
import { isClienteRole } from 'helpers/roles';
import useTypedSelector from 'hooks/useTypedSelector';
import { userSelector } from 'redux/selectors/auth.selector';

import {
  ViewContainer,
  ViewContent,
  TitleContainer,
  Title,
  TableContainer,
  CustomIconButton,
  FilePreview,
} from 'styles/view';

import { IMatzevaShowProps } from './types';
import './i18n';

const MatzevaShow: FunctionComponent<IMatzevaShowProps> = (props: IMatzevaShowProps) => {
  const { item, showTitle = true } = props;
  const user = useTypedSelector(userSelector);

  return (
    <ViewContainer auth data-testid="matzeva-show">
      <ViewContent auth>
        {showTitle && (
          <TitleContainer>
            <Title style={{ flex: 1, textAlign: 'center' }}>
              {i18next.t<string>('MATZEVA_SHOW:TITLE')}
            </Title>
            {(!isClienteRole(user) || item?.state === 'OPENED') && (
              <CustomIconButton
                onClick={() => history.push(`/public/procedures/forms/matzeva/${item?.id}/edit`)}
              >
                <EditOutlined />
              </CustomIconButton>
            )}
          </TitleContainer>
        )}
        {item && (
          <>
            <Typography variant="h6">
              {i18next.t<string>('MATZEVA_SHOW:PERSON_INFORMATION')}
            </Typography>
            <br />
            <TableContainer>
              <tbody>
                <tr>
                  <td>{i18next.t<string>('MATZEVA_SHOW:LAST_NAME')}</td>
                  <td>{item.person.lastName}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('MATZEVA_SHOW:NAME')}</td>
                  <td>{item.person.name}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('MATZEVA_SHOW:MOTHER_NAME')}</td>
                  <td>{item.person.motherName}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('MATZEVA_SHOW:DEATH_DATE')}</td>
                  <td>{item.person.deathDate}</td>
                </tr>
                <tr>
                  <td>{i18next.t<string>('MATZEVA_SHOW:DEATH_DATE_HEBREW')}</td>
                  <td>
                    {formatHebrewFromISO(item.person.deathDateHebrew) ||
                      item.person.deathDateHebrew}
                  </td>
                </tr>
              </tbody>
            </TableContainer>
            {item.cementery && (
              <>
                <Typography variant="h6">
                  {i18next.t<string>('MATZEVA_SHOW:CEMENTERY_INFORMATION')}
                </Typography>
                <br />
                <TableContainer>
                  <tbody>
                    <tr>
                      <td>{i18next.t<string>('MATZEVA_SHOW:CEMENTERY')}</td>
                      <td>{item.cementery.cementeryName || item.cementery.cementery}</td>
                    </tr>
                    <tr>
                      <td>{i18next.t<string>('MATZEVA_SHOW:LOCATION')}</td>
                      <td>{item.cementery.location}</td>
                    </tr>
                    <tr>
                      <td>{i18next.t<string>('MATZEVA_SHOW:SKETCH')}</td>
                      <td>{item.cementery.sketch}</td>
                    </tr>
                    {item.cementery.sketchFile && (
                      <tr>
                        <td>{i18next.t<string>('MATZEVA_SHOW:SKETCH_FILE')}</td>
                        <td>
                          <FilePreview src={item.cementery.sketchFile} title="sketchFile" />
                        </td>
                      </tr>
                    )}
                    {item.cementery.spanishFile && (
                      <tr>
                        <td>{i18next.t<string>('MATZEVA_SHOW:SPANISH_FILE')}</td>
                        <td>
                          <FilePreview src={item.cementery.spanishFile} title="spanishFile" />
                        </td>
                      </tr>
                    )}
                    {item.cementery.hebrewFile && (
                      <tr>
                        <td>{i18next.t<string>('MATZEVA_SHOW:HEBREW_FILE')}</td>
                        <td>
                          <FilePreview src={item.cementery.hebrewFile} title="hebrewFile" />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </TableContainer>
              </>
            )}
            {item.details && (
              <>
                <Typography variant="h6">
                  {i18next.t<string>('MATZEVA_SHOW:DETAILS_INFORMATION')}
                </Typography>
                <br />
                <TableContainer>
                  <tbody>
                    <tr>
                      <td>{i18next.t<string>('MATZEVA_SHOW:TEXT')}</td>
                      <td>{item.details.text}</td>
                    </tr>
                    {item.details.matzevaFile && (
                      <tr>
                        <td>{i18next.t<string>('MATZEVA_SHOW:MATZEVA_FILE')}</td>
                        <td>
                          <FilePreview src={item.details.matzevaFile} title="matzevaFile" />
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td>{i18next.t<string>('MATZEVA_SHOW:MARBLE_WORKER_NAME')}</td>
                      <td>{item.details.marbleWorkerName}</td>
                    </tr>
                    <tr>
                      <td>{i18next.t<string>('MATZEVA_SHOW:COLOR')}</td>
                      <td>{item.details.color}</td>
                    </tr>
                    <tr>
                      <td>{i18next.t<string>('MATZEVA_SHOW:MATERIALS')}</td>
                      <td>{item.details.materials}</td>
                    </tr>
                    <tr>
                      <td>{i18next.t<string>('MATZEVA_SHOW:DIMENSIONS')}</td>
                      <td>{item.details.dimensions}</td>
                    </tr>
                  </tbody>
                </TableContainer>
              </>
            )}
            {item.applicantName && (
              <>
                <Typography variant="h6">
                  {i18next.t<string>('MATZEVA_SHOW:APPLICANT_INFORMATION')}
                </Typography>
                <br />
                <TableContainer>
                  <tbody>
                    <tr>
                      <td>{i18next.t<string>('MATZEVA_SHOW:APPLICANT_NAME')}</td>
                      <td>{item.applicantName}</td>
                    </tr>
                  </tbody>
                </TableContainer>
              </>
            )}
            {item.comments && (
              <>
                <Typography variant="h6">{i18next.t<string>('MATZEVA_SHOW:COMMENTS')}</Typography>
                <br />
                {item.comments}
              </>
            )}
          </>
        )}
      </ViewContent>
    </ViewContainer>
  );
};

export default MatzevaShow;
